/**
 * MoyRi SWAP content — the malady→deity trade, one entry per malady subtype.
 *
 * Keyed by `${toolId}:${maladyId}` since malady ids are only unique within a
 * tool (e.g. THEMIS and NEMOS both have a 'dysnomia' subtype). Each entry:
 *
 *  - why: the survival/need story — why the malady exists (what it protects
 *    you from), NOT a flaw framing.
 *  - symptoms: plain-language self-recognition lines (no checkboxes/scoring —
 *    per Joel 2026-07-22, this is description, not a quiz).
 *  - swap: the four-row trade, malady-state → deity-state, per row:
 *      S — Situation: the cue/environment that sets it off
 *      W — Want: the need it's actually meeting (Keep/Gain/Change one of the
 *          four Drives — see DRIVE_OPTIONS)
 *      A — Action: the behavior that runs
 *      P — Payoff: what it pays out, and how the deity pays the same out
 *  - signal: how the position (−4..+4) actually moves. `automatic` is the
 *    in-app event that drives it with zero user input (per Joel 2026-07-22:
 *    the system should read data the user is already producing, not add a
 *    self-report). `fallback` is the rare weekly one-tap question used only
 *    when the automatic trace is ambiguous — never a daily quiz.
 */

export interface SwapSide {
  mal: string;
  dei: string;
}

export interface SwapEntry {
  /** `${toolId}:${maladyId}` */
  key: string;
  toolId: string;
  maladyId: string;
  maladyName: string;
  toolName: string;
  /** The deity's ability line — "the ability to ___." */
  ability: string;
  why: string;
  symptoms: string[];
  swap: {
    S: SwapSide;
    W: SwapSide;
    A: SwapSide;
    P: SwapSide;
  };
  signal: {
    automatic: string;
    fallback: string;
  };
}

/** The four Drives × Keep/Gain/Change — the standard menu for the W row. */
export const DRIVE_OPTIONS: string[] = [
  'Keep · Self — avoid failing / protect who I am',
  'Gain · Self — prove I matter / exist',
  'Change · Self — escape who I am right now',
  'Keep · Body — stay comfortable / avoid pain',
  'Gain · Body — chase a pleasure or hit',
  'Keep · Status — not look bad to the group',
  'Gain · Status — win approval / respect',
  'Keep · Possessions — protect what I have',
  'Gain · Possessions — grab more security',
];

export const SWAP_DATA: SwapEntry[] = [
  {
    key: 'telos:plane',
    toolId: 'telos',
    maladyId: 'plane',
    maladyName: 'Planē',
    toolName: 'TELOS',
    ability: 'the ability to set a condition-state aim and steer by it',
    why: 'Planē (drift) spares you the discomfort of committing to a direction — with no fixed aim you can never be "wrong." The cost is years of motion with no progress.',
    symptoms: [
      'Busy, but can’t say what it’s all for',
      'Goals feel arbitrary, so you avoid setting them',
      'Direction changes with your mood',
      '"Where am I even going?" feels familiar',
    ],
    swap: {
      S: { mal: 'An open, shapeless day with no set destination', dei: 'Read the fixed aim first thing — lock-screen it' },
      W: { mal: 'Keep · Self — dodge the failure of committing to a wrong aim', dei: 'Anchor identity to one aim worth chasing' },
      A: { mal: 'Drift into whatever task is nearest', dei: 'Pick the next action that serves the aim' },
      P: { mal: 'Relief from the pressure of committing', dei: 'The calm of being on-course — same relief, earned' },
    },
    signal: {
      automatic: 'Aim set/updated this week, and referenced when KYRS/METIS planned a day.',
      fallback: 'Only if ambiguous: "Did this week’s aim actually shape what you did?"',
    },
  },
  {
    key: 'oracle:amathia',
    toolId: 'oracle',
    maladyId: 'amathia',
    maladyName: 'Amathia',
    toolName: 'ORACLE',
    ability: 'the skill of taking honest counsel — reading where you truly stand and letting intake questions prescribe the work',
    why: 'Amathia (ignorance) spares you the discomfort of a clear-eyed look — the uncounseled state feels lighter than asking where you actually stand. The cost is flying blind rather than asking, and correcting late instead of early.',
    symptoms: [
      'You act first and only assess afterward, if ever',
      'Intake questions feel like busywork, not information',
      'You’re routinely surprised by problems that were visible in hindsight',
      '"I didn’t see that coming" is a recurring line',
    ],
    swap: {
      S: { mal: 'A new week or task with no honest read taken first', dei: 'The intake review run before committing to anything' },
      W: { mal: 'Change · Self — avoid the discomfort of an honest self-read', dei: 'Gain · Self — build real judgment about your own position' },
      A: { mal: 'Skip the read and guess, or just start moving', dei: 'Take the intake, let it prescribe the next move' },
      P: { mal: 'The comfort of not having to look', dei: 'Fewer blind-sided weeks, and a plan that actually fits reality' },
    },
    signal: {
      automatic: 'Whether an ORACLE intake was taken before the week/gear started, and whether its output was acted on.',
      fallback: 'Only if no intake landed in 2+ weeks: "Have you checked where you actually stand lately?"',
    },
  },
  {
    key: 'astra:dysastria',
    toolId: 'astra',
    maladyId: 'dysastria',
    maladyName: 'Dysastria',
    toolName: 'ASTRA',
    ability: 'the skill of holding all five life sectors in one view and steering by the whole compass',
    why: 'Dysastria (off-star) lets you pour everything into the one sector that’s loudest right now — total focus there feels responsible, even virtuous. The cost is the other four sectors drifting unwatched until the neglect becomes its own emergency.',
    symptoms: [
      'One life sector eats all your attention for weeks at a time',
      'You’re blindsided by a sector you hadn’t checked on in a while',
      '"I forgot I even had this to manage" is a familiar feeling',
      'Balance only gets attention after something already broke',
    ],
    swap: {
      S: { mal: 'A demanding sector pulling all your focus', dei: 'The compass review that shows all five sectors at once' },
      W: { mal: 'Keep · Self — protect against failing the loudest thing right now', dei: 'Keep · Self — protect the whole life, not just the loudest part of it' },
      A: { mal: 'Pour all effort into the one loud sector, ignore the rest', dei: 'Steer by the whole compass, giving each sector its due' },
      P: { mal: 'The relief of momentary focus on what’s urgent', dei: 'No sector left to rot — fewer emergencies born of neglect' },
    },
    signal: {
      automatic: 'Whether a weekly ASTRA compass review ran, and the spread of attention/activity logged across all five sectors.',
      fallback: 'Only if one sector shows zero activity for 3+ weeks: "Still watching every sector, or has one gone quiet?"',
    },
  },
  {
    key: 'numa:oizys',
    toolId: 'numa',
    maladyId: 'oizys',
    maladyName: 'Oizys',
    toolName: 'NUMA',
    ability: 'the ability to match your load to the energy you actually have',
    why: 'Oizys (depletion) kept you "productive" by overriding the empty gauge — running on adrenaline felt safer than admitting the tank was dry. It buys today’s output with next week’s collapse.',
    symptoms: [
      'You stop only when you physically can’t go on',
      'Rest makes you feel lazy or guilty',
      'Energy reads as either 100% or flat zero',
      'Burnout arrives on a predictable cycle',
    ],
    swap: {
      S: { mal: 'A big to-do list, or a second wind arriving late at night', dei: 'Check the gauge before adding anything to the day' },
      W: { mal: 'Gain · Status — prove your worth through output', dei: 'Gain · Status — earn respect for sustainable pace' },
      A: { mal: 'Override the gauge and push past empty', dei: 'Load the day to what the gauge shows, no further' },
      P: { mal: 'A short high of feeling indispensable', dei: 'The same sense of capability, minus the crash' },
    },
    signal: {
      automatic: 'Daily energy check-in logged in NUMA, and whether the day’s committed load stayed within it.',
      fallback: 'Only if no check-in landed: "Did today run past what you actually had?"',
    },
  },
  {
    key: 'numa:phthisis',
    toolId: 'numa',
    maladyId: 'phthisis',
    maladyName: 'Phthisis',
    toolName: 'NUMA',
    ability: 'the ability to match your load to the energy you actually have',
    why: 'Phthisis (wasting) lets capacity leak away unnoticed — nothing dramatic ever forces the issue, so it never gets addressed.',
    symptoms: [
      'A slow decline you can’t point to a cause for',
      'Reserves feel thinner every month, quietly',
      'No single bad day explains it',
    ],
    swap: {
      S: { mal: 'Weeks passing with no capacity check', dei: 'A standing weekly capacity read' },
      W: { mal: 'Change · Self — avoid confronting a slow decline', dei: 'Keep · Self — protect long-run capacity on purpose' },
      A: { mal: 'Let the trend go unmeasured', dei: 'Track the trend and adjust load before it bites' },
      P: { mal: 'Avoids a hard conversation with yourself', dei: 'Catches the slide early, cheaply' },
    },
    signal: {
      automatic: 'Trendline across weekly NUMA check-ins (declining vs. holding).',
      fallback: 'Only on 3+ weeks with no check-in: "Still tracking your capacity?"',
    },
  },
  {
    key: 'numa:aergia',
    toolId: 'numa',
    maladyId: 'aergia',
    maladyName: 'Aergia',
    toolName: 'NUMA',
    ability: 'the ability to match your load to the energy you actually have',
    why: 'Aergia (idleness) protects you from ever finding your real ceiling — if you never push, you never learn you can’t.',
    symptoms: [
      'You under-load even on high-energy days',
      'Comfortable pace has quietly become the only pace',
      'Ambition and capacity have stopped talking to each other',
    ],
    swap: {
      S: { mal: 'A day you know could take more, left light anyway', dei: 'Set today’s load to match the gauge, not the habit' },
      W: { mal: 'Keep · Body — stay comfortable', dei: 'Gain · Self — prove you can meet the real capacity' },
      A: { mal: 'Cap effort well below the gauge', dei: 'Load to what the gauge actually allows' },
      P: { mal: 'Safety from ever failing at a stretch', dei: 'Real momentum, and a truer read on your ceiling' },
    },
    signal: {
      automatic: 'Committed load vs. logged energy gauge — persistent under-loading trend.',
      fallback: 'Only if trend holds 2+ weeks: "Could today have taken more than you gave it?"',
    },
  },
  {
    key: 'moyri:akrasia',
    toolId: 'moyri',
    maladyId: 'akrasia',
    maladyName: 'Akrasia',
    toolName: 'MoyRi',
    ability: 'the ability to engineer a skill-system that produces an outcome',
    why: 'Akrasia (weak will) hands the wheel to the current mood so you never have to face discomfort — and never become who the system was built for.',
    symptoms: [
      'You know what to do and don’t do it',
      'The mood decides, not the plan',
      'Systems feel like cages you break within days',
    ],
    swap: {
      S: { mal: 'The urge vs. the committed plan', dei: 'A pre-built system that doesn’t ask for willpower' },
      W: { mal: 'Change · Self — escape the discomfort of the moment', dei: 'Keep · Self — protect the identity you’re building' },
      A: { mal: 'Let the current feeling override the plan', dei: 'Run the system regardless of mood' },
      P: { mal: 'Instant relief from discomfort', dei: 'A system you can trust, so relief stops being the only prize' },
    },
    signal: {
      automatic: 'Whether the committed MoyRi system ran as scheduled, per METIS done/not-done.',
      fallback: 'None — METIS’s existing daily tap covers this fully.',
    },
  },
  {
    key: 'moyri:kakia',
    toolId: 'moyri',
    maladyId: 'kakia',
    maladyName: 'Kakia',
    toolName: 'MoyRi',
    ability: 'the ability to engineer a skill-system that produces an outcome',
    why: 'Kakia (the easy-bad choice) lets you feel like you’re choosing, when really the path of least resistance is choosing for you.',
    symptoms: [
      'You default to whatever’s easiest, then justify it after',
      'The harder-but-right option gets quietly filtered out',
      'Standards slip and you barely notice the slide',
    ],
    swap: {
      S: { mal: 'A choice between the easy option and the built system', dei: 'The system already removed the choice' },
      W: { mal: 'Keep · Body — minimize effort now', dei: 'Gain · Self — build the version of you that follows through' },
      A: { mal: 'Take the path of least resistance', dei: 'Follow the pre-committed system' },
      P: { mal: 'Immediate ease', dei: 'Ease later, from not having to decide in the moment' },
    },
    signal: {
      automatic: 'Rate of system-adherence vs. ad-hoc substitutions logged in MoyRi.',
      fallback: 'Only on a broken streak: "Did you follow the system or the easy way out?"',
    },
  },
  {
    key: 'moyri:adrasteia',
    toolId: 'moyri',
    maladyId: 'adrasteia',
    maladyName: 'Adrasteia',
    toolName: 'MoyRi',
    ability: 'the ability to engineer a skill-system that produces an outcome',
    why: 'Adrasteia (the inescapable grind) turns the system rigid — no slack means one bad week feels like total failure, so you quit rather than flex.',
    symptoms: [
      'One missed day and the whole system feels ruined',
      'No room for a bad week in how it’s built',
      'All-or-nothing has ended more habits than laziness has',
    ],
    swap: {
      S: { mal: 'A missed day inside a system with zero slack', dei: 'A system built with the 5th-slot miss already priced in' },
      W: { mal: 'Keep · Self — protect a perfect record', dei: 'Keep · Self — protect the habit itself, not the streak' },
      A: { mal: 'Abandon the whole system after one miss', dei: 'Use the built-in miss, keep going' },
      P: { mal: 'Escape from the pressure of a rigid system', dei: 'A system that survives contact with a real life' },
    },
    signal: {
      automatic: 'Whether a single missed day is followed by continued use (recovery) vs. abandonment.',
      fallback: 'Only after a miss: "Still in the system, or did one bad day end it?"',
    },
  },
  {
    key: 'hrmez:homados',
    toolId: 'hrmez',
    maladyId: 'homados',
    maladyName: 'Homados',
    toolName: 'HRMEZ',
    ability: 'the ability to hold steady signal through noise',
    why: 'Homados (din) lets you feel engaged by staying plugged into the noise — busywork drowns out the harder quiet.',
    symptoms: [
      'Always reachable, never settled',
      'Silence feels uncomfortable',
      'You chase every ping',
    ],
    swap: {
      S: { mal: 'A notification, or a quiet moment that feels wrong', dei: 'A scheduled inbox window instead of always-on' },
      W: { mal: 'Gain · Status — feel needed, in-demand', dei: 'Gain · Status — respected for depth, not availability' },
      A: { mal: 'Drop everything for the ping', dei: 'Batch and clear on the scheduled pass' },
      P: { mal: 'A hit of feeling important', dei: 'Actual finished work, which reads as more important' },
    },
    signal: {
      automatic: 'Inbox-age / response-latency pattern inside HRMEZ.',
      fallback: 'None — inbox-age is fully automatic.',
    },
  },
  {
    key: 'hrmez:oistros',
    toolId: 'hrmez',
    maladyId: 'oistros',
    maladyName: 'Oistros',
    toolName: 'HRMEZ',
    ability: 'the ability to hold steady signal through noise',
    why: 'Oistros (the sting/frenzy) turns every new input into an emergency, because treating nothing as urgent once let something slip.',
    symptoms: [
      'Everything feels urgent the moment it arrives',
      'You can’t tell real fires from noise anymore',
      'Reactive mode has become the resting state',
    ],
    swap: {
      S: { mal: 'A new item lands and reads as an emergency', dei: 'A held triage pass before reacting' },
      W: { mal: 'Keep · Self — never be the one who missed something', dei: 'Keep · Self — be the one who stayed steady' },
      A: { mal: 'React to everything as if it’s on fire', dei: 'Triage, then act only on what’s real' },
      P: { mal: 'The adrenaline of urgency', dei: 'The calm of being actually in control' },
    },
    signal: {
      automatic: 'Ratio of items acted on immediately vs. triaged, tracked in HRMEZ.',
      fallback: 'Only on a spike: "Did everything today feel equally urgent?"',
    },
  },
  {
    key: 'kyrs:koalemos',
    toolId: 'kyrs',
    maladyId: 'koalemos',
    maladyName: 'Koalemos',
    toolName: 'KYRS',
    ability: 'the ability to schedule based on priority',
    why: 'Koalemos lets you feel decisive by grabbing the nearest task — no agonizing over priority. The cost is staying busy with the wrong thing.',
    symptoms: [
      'The loudest task wins, not the biggest',
      'You start before deciding what matters',
      'End of day: busy, big rock untouched',
    ],
    swap: {
      S: { mal: 'A pile of tasks with no ranking', dei: 'The scored priority list, already built' },
      W: { mal: 'Change · Self — avoid the discomfort of deciding', dei: 'Gain · Self — prove you can follow through on what matters' },
      A: { mal: 'Grab whatever is nearest or loudest', dei: 'Work top-down the scored list' },
      P: { mal: 'The relief of motion, immediately', dei: 'The relief of the big rock actually moving' },
    },
    signal: {
      automatic: 'Whether the day’s completed tasks match the top of the KYRS priority order.',
      fallback: 'None — order-vs-completion is fully automatic.',
    },
  },
  {
    key: 'kyrs:oknos',
    toolId: 'kyrs',
    maladyId: 'oknos',
    maladyName: 'Oknos',
    toolName: 'KYRS',
    ability: 'the ability to schedule based on priority',
    why: 'Oknos (hesitation) keeps the list scored but never worked — ranking feels like progress, so acting on the ranking never quite happens.',
    symptoms: [
      'The list is beautifully organized and barely touched',
      'Planning has quietly replaced doing',
      'Priority #1 rotates without ever getting done',
    ],
    swap: {
      S: { mal: 'The scored list sits open, unstarted', dei: 'A hard start-time attached to the top item' },
      W: { mal: 'Keep · Self — stay safe by only ever preparing', dei: 'Gain · Self — build proof of follow-through' },
      A: { mal: 'Re-sort and re-plan instead of starting', dei: 'Start the top item at its set time' },
      P: { mal: 'The comfort of feeling organized', dei: 'The real comfort of things actually finishing' },
    },
    signal: {
      automatic: 'Time between a task entering the top rank and being marked done in METIS.',
      fallback: 'Only if the top item stalls 3+ days: "Still the priority, or stuck?"',
    },
  },
  {
    key: 'krnos:chaos',
    toolId: 'krnos',
    maladyId: 'chaos',
    maladyName: 'Chaos',
    toolName: 'KRNOS',
    ability: 'the ability to build a realistic week and reschedule when reality changes',
    why: 'Chaos lets you keep every option open by never committing a week to paper — nothing can be "wrong" if nothing was ever planned.',
    symptoms: [
      'The week has no shape until it’s already happening',
      'Rescheduling feels like admitting failure, so you avoid planning at all',
      'Every week looks like the last one, unplanned',
    ],
    swap: {
      S: { mal: 'A week starting with no build', dei: 'The week built before Monday, reschedule allowed by design' },
      W: { mal: 'Keep · Self — avoid the risk of a broken plan', dei: 'Keep · Self — protect a plan built to flex' },
      A: { mal: 'Let the week assemble itself reactively', dei: 'Build it, then deliberately reschedule when reality shifts' },
      P: { mal: 'Freedom from ever being wrong', dei: 'A week that actually gets somewhere' },
    },
    signal: {
      automatic: 'Whether a week was built in KRNOS before it started, and whether reschedules were logged (not silent).',
      fallback: 'None — build/reschedule events are automatic.',
    },
  },
  {
    key: 'janus:aporia',
    toolId: 'janus',
    maladyId: 'aporia',
    maladyName: 'Aporia',
    toolName: 'JANUS',
    ability: 'the ability to make criteria-based, informed decisions',
    why: 'Aporia (impasse) protects you from a wrong choice by never finishing the choice — indecision feels safer than being provably wrong.',
    symptoms: [
      'Decisions get reopened long after they should be closed',
      'You gather more information instead of deciding',
      'The same choice returns to your mind on a loop',
    ],
    swap: {
      S: { mal: 'A decision with no clear criteria written down', dei: 'Criteria set before you look at the options' },
      W: { mal: 'Keep · Self — avoid being provably wrong', dei: 'Gain · Self — trust your own judgment' },
      A: { mal: 'Re-litigate the decision indefinitely', dei: 'Decide against the criteria, then close it' },
      P: { mal: 'Never having to own a bad outcome', dei: 'A closed loop and the confidence that comes with it' },
    },
    signal: {
      automatic: 'Whether a decision logged in JANUS stays closed for its review window, or gets reopened.',
      fallback: 'Only on reopen: "What changed, or is this the old indecision?"',
    },
  },
  {
    key: 'tkne:amechania',
    toolId: 'tkne',
    maladyId: 'amechania',
    maladyName: 'Amechania',
    toolName: 'TKNE',
    ability: 'the ability to turn intent into a concrete sequence',
    why: 'Amechania (helplessness) lets "I don’t know how" excuse never starting — not knowing feels safer than trying and failing.',
    symptoms: [
      '"I don’t know where to start" is a constant',
      'Goals stay abstract, never broken down',
      'Overwhelm defaults to doing nothing',
    ],
    swap: {
      S: { mal: 'A big vague goal with no obvious first step', dei: 'The goal already broken into milestones + next action' },
      W: { mal: 'Change · Self — avoid the risk of trying and failing', dei: 'Gain · Self — build proof you can figure things out' },
      A: { mal: 'Freeze, or avoid the project entirely', dei: 'Take the one next concrete action' },
      P: { mal: 'Safety from a visible failed attempt', dei: 'Real movement, how ever small' },
    },
    signal: {
      automatic: 'Whether a project in TKNE has a defined next action, and whether it moved this week.',
      fallback: 'Only if stalled 2+ weeks: "Still moving on this, or stuck on where to start?"',
    },
  },
  {
    key: 'tkne:atlas',
    toolId: 'tkne',
    maladyId: 'atlas',
    maladyName: 'Atlas',
    toolName: 'TKNE',
    ability: 'the ability to turn intent into a concrete sequence',
    why: 'Atlas (the world on your shoulders) refuses to break a project down because carrying it whole feels like the only way to prove it matters.',
    symptoms: [
      'Projects stay one giant undifferentiated task',
      'Delegating or splitting the work feels like losing control',
      'Everything rides on you alone finishing it all',
    ],
    swap: {
      S: { mal: 'A project held as one unbreakable whole', dei: 'The same project, pre-split into milestones' },
      W: { mal: 'Gain · Status — be the one who carried it all', dei: 'Gain · Status — be the one who actually shipped it' },
      A: { mal: 'Carry the whole undivided load yourself', dei: 'Work one milestone at a time' },
      P: { mal: 'The identity of the indispensable one', dei: 'The same identity, minus the burnout' },
    },
    signal: {
      automatic: 'Whether TKNE projects have milestones defined vs. one monolithic entry.',
      fallback: 'None — milestone-structure is read directly from the data.',
    },
  },
  {
    key: 'metis:ate',
    toolId: 'metis',
    maladyId: 'ate',
    maladyName: 'Atē',
    toolName: 'METIS',
    ability: 'the ability to lay a day into sequenced slots',
    why: 'Atē (ruin/rashness) leaves the day open on purpose — no slots means no accountability, and no output either.',
    symptoms: [
      'Days have no shape before they start',
      'Time leaks away unaccounted for',
      '"Where did the day go?" is a familiar question',
    ],
    swap: {
      S: { mal: 'A blank day with no morning plan', dei: 'The day slotted before the first task starts' },
      W: { mal: 'Change · Self — escape the discomfort of committing to a shape', dei: 'Keep · Self — protect a day that actually delivers' },
      A: { mal: 'React all day, plan nothing', dei: 'Follow the slotted agenda, resuming after disruptions' },
      P: { mal: 'The freedom of no obligations', dei: 'The freedom of a day that actually got somewhere' },
    },
    signal: {
      automatic: 'Whether today’s slots were set, and the done/not-done tap at day’s end (the existing METIS review).',
      fallback: 'None — this is the daily pass/fail already built into METIS.',
    },
  },
  {
    key: 'themis:dysnomia',
    toolId: 'themis',
    maladyId: 'dysnomia',
    maladyName: 'Dysnomia',
    toolName: 'THEMIS',
    ability: 'the ability to break any action into an ordered protocol',
    why: 'Dysnomia (disorder) lets you dodge the effort of sequencing — winging it feels lighter than a protocol, until it costs you redone work.',
    symptoms: [
      'You jump in with no first step defined',
      'Work gets done out of order, then redone',
      '"Just wing it" is the default',
    ],
    swap: {
      S: { mal: 'A messy action ahead, impatience to start', dei: 'The protocol pulled up before starting' },
      W: { mal: 'Keep · Body — avoid the friction of a formal step', dei: 'Gain · Self — build reliable execution' },
      A: { mal: 'Wing it, no fixed order', dei: 'Run the ordered protocol, step by step' },
      P: { mal: 'A faster-feeling start', dei: 'A finish that doesn’t need redoing' },
    },
    signal: {
      automatic: 'Whether a THEMIS protocol was opened before the action, and steps completed vs. silently skipped.',
      fallback: 'None — step-skip is logged automatically.',
    },
  },
  {
    key: 'clio:lethe',
    toolId: 'clio',
    maladyId: 'lethe',
    maladyName: 'Lethe',
    toolName: 'CLIO',
    ability: 'the ability to close a loop and carry the lesson forward',
    why: 'Lethe (forgetting) spares you the sting of reviewing misses — not looking back means never facing the mistake, at the cost of repeating it.',
    symptoms: [
      'You rarely review the day or week',
      'The same mistakes recur',
      'Wins evaporate — hard to say what worked',
    ],
    swap: {
      S: { mal: 'End of day, no review taken', dei: 'The standing morning + night CLIO session' },
      W: { mal: 'Change · Self — avoid the sting of facing a miss', dei: 'Gain · Self — build a track record of improving' },
      A: { mal: 'Skip the review, move straight to tomorrow', dei: 'Log the lesson and let it change one thing' },
      P: { mal: 'Avoids short-term discomfort', dei: 'Fewer repeated mistakes, compounding' },
    },
    signal: {
      automatic: 'Whether a CLIO session ran today, and whether a lesson from it changed something the following week.',
      fallback: 'None — session-taken is a direct log event.',
    },
  },
  {
    key: 'argos:achlys',
    toolId: 'argos',
    maladyId: 'achlys',
    maladyName: 'Achlys',
    toolName: 'ARGOS',
    ability: 'the ability to watch the whole field and catch drift early',
    why: 'Achlys (mist) keeps the picture blurry on purpose — you can’t be forced to act on a hard truth you’ve refused to see clearly.',
    symptoms: [
      'Problems seem to appear "out of nowhere"',
      'You avoid checking the numbers',
      'Bad news gets discovered late',
    ],
    swap: {
      S: { mal: 'A metric you’ve been avoiding', dei: 'The standing weekly field scan' },
      W: { mal: 'Change · Self — avoid an uncomfortable truth', dei: 'Keep · Self — protect your ability to act early' },
      A: { mal: 'Avoid the check, stay foggy', dei: 'Run the scan, face what it shows' },
      P: { mal: 'Short-term comfort', dei: 'Small corrections instead of big rescues' },
    },
    signal: {
      automatic: 'Whether a weekly ARGOS scan ran, and time-to-detection on flagged drift.',
      fallback: 'None — scan-taken is a direct log event.',
    },
  },
  {
    key: 'argos:apate',
    toolId: 'argos',
    maladyId: 'apate',
    maladyName: 'Apatē',
    toolName: 'ARGOS',
    ability: 'the ability to watch the whole field and catch drift early',
    why: 'Apatē (self-deception) lets a scan happen but reads it charitably every time — the numbers get seen, then explained away.',
    symptoms: [
      'You review the data but always find a reason it’s fine',
      'Warning signs get reframed as one-offs, repeatedly',
      'The scan happens; the correction never does',
    ],
    swap: {
      S: { mal: 'A scan result that should trigger a change', dei: 'A pre-committed threshold that forces action' },
      W: { mal: 'Keep · Self — protect the story that things are fine', dei: 'Keep · Self — protect your credibility with yourself' },
      A: { mal: 'Rationalize the result away', dei: 'Act once the threshold is crossed, no re-litigating' },
      P: { mal: 'Comfort of an unbroken narrative', dei: 'Real course-correction before it compounds' },
    },
    signal: {
      automatic: 'Whether a flagged threshold in ARGOS was followed by a logged change, or by nothing.',
      fallback: 'Only if a threshold is crossed with no follow-up: "That flag last week — action taken?"',
    },
  },
  {
    key: 'nemos:dysnomia',
    toolId: 'nemos',
    maladyId: 'dysnomia',
    maladyName: 'Dysnomia',
    toolName: 'NEMOS',
    ability: 'the ability to file and retrieve without loss',
    why: 'Dysnomia (disorder), here, lets filing slide because a mess feels lower-effort in the moment — until retrieval becomes its own project.',
    symptoms: [
      'Things go in "somewhere" instead of a system',
      'Finding something takes longer every month',
      'Duplicates and orphaned items pile up quietly',
    ],
    swap: {
      S: { mal: 'A new item with no obvious home', dei: 'The filing system already has a slot for it' },
      W: { mal: 'Keep · Body — avoid the small effort of filing correctly', dei: 'Gain · Possessions — protect fast access to what you own' },
      A: { mal: 'Drop it wherever’s fastest', dei: 'File it to the system, once' },
      P: { mal: 'A few saved seconds now', dei: 'Minutes saved every future retrieval' },
    },
    signal: {
      automatic: 'Misfile/duplicate rate and retrieval-time trend inside NEMOS.',
      fallback: 'None — fully automatic from filing events.',
    },
  },
];

const BY_KEY = new Map(SWAP_DATA.map((e) => [e.key, e]));

export function getSwapEntry(toolId: string, maladyId: string): SwapEntry | undefined {
  return BY_KEY.get(`${toolId}:${maladyId}`);
}
